import { useReselector } from '@/utils/sharedHooks'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { selectFederalEstimateByTaxQuarter } from '@/features/taxes/quarterly/userTaxEstimates.selector'
import useTaxPaymentInfoFlyout from '../TaxPaymentInfoFlyout'
import { QuarterlyTaxEstimateDetail } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'

import { UpdateQuarterlyTaxEstimateDialog } from '../UpdateQuarterlyTaxEstimateDialog'
import { TypographyStyles } from '@/styles'
import { Button, Image, Link, Text } from '@/ui'
import { useAppDispatch } from '@/utils/typeHelpers'
import { CatchUpSummary } from './CatchUpSummary'
import { useSafeHarborCatchUpEnabled } from './helpers'
import {
  fetchSafeHarborCatchUp,
  selectSafeHarborCatchUp,
} from '../../safeHarborCatchUp.slice'

export const FinishCalculateSafeHarbor = ({
  details,
}: {
  details?: QuarterlyTaxEstimateDetail | null
}) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const catchUpEnabled = useSafeHarborCatchUpEnabled()

  // Fetched here as well as on the step: a Q1 target skips the step entirely,
  // and the per-quarter figure is only written just before this screen.
  useEffect(() => {
    if (catchUpEnabled) {
      dispatch(fetchSafeHarborCatchUp())
    }
  }, [dispatch, catchUpEnabled])

  // Discard a payload for another quarter, matching the card. The store can
  // still hold the previous quarter's figures while the mount fetch is in
  // flight, and the flyout header names the quarter, so a mismatch is visible.
  const payload = useReselector(selectSafeHarborCatchUp)
  const catchUpPayload =
    catchUpEnabled &&
    payload?.taxYear === details?.taxYear &&
    payload?.taxQuarter === details?.taxQuarter
      ? payload
      : null

  const federalTaxEstimate = useReselector(
    selectFederalEstimateByTaxQuarter,
    `${details?.taxYear}-${details?.taxQuarter}`
  )

  const [estimateModalOpen, setEstimateModalOpen] = useState(false)
  const openFlyout = useTaxPaymentInfoFlyout({
    estimateId: federalTaxEstimate?.id,
    openQTEModal: () => setEstimateModalOpen(true),
    amountInCentsOverride: catchUpPayload?.catchUp?.totalDueNowInCents,
    headerOverride: catchUpPayload?.catchUp
      ? `Q${catchUpPayload.taxQuarter} ${catchUpPayload.taxYear} catch-up payment`
      : undefined,
  })

  return (
    <div className="space-y-6 flex flex-col items-center max-w-3xl mx-auto text-center">
      <Text variant={TypographyStyles.DISPLAY_SM}>You&apos;re done!</Text>

      <Text variant={TypographyStyles.BODY_MD}>
        Now you can either proceed to payment (we&apos;ll walk you through how
        to do this), or if you&apos;re not ready yet, you can go back to the
        Quarterly Tax Center.
      </Text>

      {catchUpPayload?.catchUp && (
        <div className="w-full max-w-md text-left">
          <CatchUpSummary catchUp={catchUpPayload.catchUp} />
        </div>
      )}

      <Text variant={TypographyStyles.BODY_MD}>
        Made a mistake? Go back and{' '}
        <Link href="/taxes/quarterly/calculate-safe-harbor">edit</Link>
      </Text>

      <Image
        src="https://heard-images.s3.amazonaws.com/assets/calculator2.svg"
        alt="Calculator"
        className="h-[180px] w-[180px]"
      />

      <div className="flex flex-col justify-center gap-4">
        {federalTaxEstimate && (
          <Button className="w-full" onClick={() => openFlyout()}>
            Pay Now
          </Button>
        )}

        <Button
          className="w-full"
          variant="secondary"
          onClick={() => navigate('/taxes/quarterly')}
        >
          Return to Quarterly Taxes
        </Button>
      </div>

      {federalTaxEstimate && (
        <UpdateQuarterlyTaxEstimateDialog
          open={estimateModalOpen}
          close={() => setEstimateModalOpen(false)}
          estimate={federalTaxEstimate}
          amountInCentsOverride={catchUpPayload?.catchUp?.totalDueNowInCents}
        />
      )}
    </div>
  )
}
